'use client';

import { useEffect, useRef } from 'react';
import { useAuth } from '../../../../auth/state/use-auth.js';
import { useTurnkeySession } from '../../../../generated/mutations/useTurnkeySession.gen.js';
import { useSdkTurnkey } from '../turnkey/use-sdk-turnkey.js';
function authRealm(network) {
  return network === 'mainnet' ? 'mainnet' : 'test';
}
// ── Profile fetch via bare fetch ──────────────────────────────────────────────
async function fetchMeForNetworkBridge() {
  try {
    const res = await fetch('/v1/me', {
      credentials: 'include'
    });
    if (res.status === 401 || res.status === 403) return null;
    if (!res.ok) return null;
    const json = await res.json();
    const raw = json !== null && typeof json === 'object' && 'data' in json ? json.data : json;
    if (raw === null || typeof raw !== 'object') return null;
    const p = raw;
    return {
      id: typeof p.id === 'string' ? p.id : '',
      email: typeof p.email === 'string' ? p.email : '',
      phoneNumber: typeof p.phoneNumber === 'string' ? p.phoneNumber : null,
      displayName: typeof p.displayName === 'string' ? p.displayName : null,
      handle: typeof p.handle === 'string' ? p.handle : null,
      avatarUrl: typeof p.avatarUrl === 'string' ? p.avatarUrl : null,
      avatarNsfw: typeof p.avatarNsfw === 'boolean' ? p.avatarNsfw : null,
      avatarPreferences: p.avatarPreferences !== null && typeof p.avatarPreferences === 'object' && typeof p.avatarPreferences.rasterise === 'boolean' ? {
        rasterise: p.avatarPreferences.rasterise
      } : null,
      heroUrl: typeof p.heroUrl === 'string' ? p.heroUrl : null,
      coverNsfw: typeof p.heroNsfw === 'boolean' ? p.heroNsfw : null,
      bio: typeof p.bio === 'string' ? p.bio : null,
      country: typeof p.country === 'string' ? p.country : null,
      locationSource: typeof p.locationSource === 'string' ? p.locationSource : null,
      createdAt: typeof p.createdAt === 'string' ? p.createdAt : new Date().toISOString(),
      onboardingCompleted: p.onboardingCompleted === true,
      connectedProviders: Array.isArray(p.connectedProviders) ? p.connectedProviders.map(cp => ({
        provider: typeof cp.provider === 'string' ? cp.provider : '',
        ...(typeof cp.username === 'string' ? {
          username: cp.username
        } : {}),
        ...(typeof cp.displayName === 'string' ? {
          displayName: cp.displayName
        } : {})
      })) : [],
      turnkeySubOrgId: typeof p.turnkeySubOrgId === 'string' ? p.turnkeySubOrgId : null
    };
  } catch {
    return null;
  }
}
function extractEmbeddedWallets(turnkeyWallets, activeOrgId) {
  /* v8 ignore start -- defensive: sole caller returns on empty activeOrgId before calling */
  if (activeOrgId === undefined || activeOrgId === '') return [];
  /* v8 ignore stop */
  return turnkeyWallets.filter(w => w.source === 'embedded').flatMap(w => w.accounts.filter(a => a.organizationId === activeOrgId).map(a => ({
    walletId: w.walletId,
    address: a.address
  })));
}
// ── Component ─────────────────────────────────────────────────────────────────
/**
 * Side-effect-only component. Renders null.
 *
 * Mount once inside the TurnkeyProvider + SDKProvider trees. When the `network`
 * prop changes across an auth-realm boundary (test ↔ mainnet), silently
 * re-establishes the Morfi session against the new backend.
 */
export function SdkNetworkAuthBridge({
  network
}) {
  const {
    wallets,
    walletProviders,
    session,
    user
  } = useSdkTurnkey();
  const {
    setUser
  } = useAuth();
  const turnkeySession = useTurnkeySession();
  const lastSyncedNetworkRef = useRef(null);
  const inFlightRef = useRef(false);
  useEffect(() => {
    // Initial mount: record the current network and skip.
    if (lastSyncedNetworkRef.current === null) {
      lastSyncedNetworkRef.current = network;
      return;
    }
    if (lastSyncedNetworkRef.current === network) return;
    const previousNetwork = lastSyncedNetworkRef.current;
    lastSyncedNetworkRef.current = network;
    // Realm guard: only re-auth on test↔mainnet crossing.
    if (authRealm(previousNetwork) === authRealm(network)) return;
    const turnkeySessionJwt = session?.token;
    const activeOrgId = session?.organizationId;
    if (turnkeySessionJwt === undefined || activeOrgId === undefined || activeOrgId === '') return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const embeddedWallets = extractEmbeddedWallets(wallets, activeOrgId);
    const externalWallet = walletProviders?.flatMap(p => p.connectedAddresses)[0];
    void (async () => {
      try {
        await turnkeySession.mutateAsync({
          turnkeySessionJwt,
          email: user?.userEmail ?? null,
          embeddedWallets,
          externalWalletAddress: externalWallet ?? null
        });
        const profile = await fetchMeForNetworkBridge();
        if (profile !== null) {
          setUser(profile);
        }
      } catch {
        // SILENT-CATCH: Keep the prior user state. The SDK is already rebound
        // to the new origin — if the previous session cookie is genuinely
        // invalid there, the next API call's 401 will trigger the SDK's
        // authoritative logout. Premature `setUser(null)` here disrupts UX
        // on transient failures.
      } finally {
        inFlightRef.current = false;
      }
    })();
  }, [network, session, wallets, walletProviders, user, setUser, turnkeySession.mutateAsync]);
  return null;
}